import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogAlert,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTransactionModal } from "@/context/transaction-modal-provider";
import useTransactions from "@/hooks/use-transactions";
import { Category, Transaction } from "@/types/transaction.type";
import dayjs from "dayjs";
import { CirclePlus, SquarePen, Trash } from "lucide-react";
import React from "react";
import TransactionForm from "./form";
import { formatCurrencyWithCents } from "@/utils/currency";

const TransactionModal: React.FC = () => {
  const { state, hide } = useTransactionModal();
  const {
    createTransactionMutation,
    editTransactionMutation,
    deleteTransactionMutation,
  } = useTransactions({ fetchEnabled: false });

  const handleCreate = async (data: Partial<Transaction>) => {
    await createTransactionMutation.mutateAsync(data);
    hide();
  };

  const handleEdit = async (data: Partial<Transaction>) => {
    if (state.transaction) {
      await editTransactionMutation.mutateAsync(data);
    }
    hide();
  };

  const handleDelete = async () => {
    if (state.transaction) {
      await deleteTransactionMutation.mutateAsync(state.transaction);
    }
    hide();
  };

  const getTitle = () => {
    if (state.mode === "edit") {
      return (
        <DialogAlert
          title="Edit Transaction"
          icon={
            <div className="p-2 bg-black-400/10 rounded-full flex justify-center items-center">
              <SquarePen size={24} />
            </div>
          }
        />
      );
    } else if (state.mode === "create") {
      return (
        <DialogAlert
          title="Create Transaction"
          icon={
            <div className="p-2 bg-black-400/10 rounded-full flex justify-center items-center">
              <CirclePlus size={24} />
            </div>
          }
        />
      );
    } else if (state.mode === "delete") {
      return (
        <DialogAlert
          title="Are you sure?"
          description={
            <>
              You are about to delete the following transaction:
              <br />
              <span className="font-semibold">
                "{state.transaction?.occurred} -{" "}
                {formatCurrencyWithCents(state.transaction?.amount ?? 0)} -{" "}
                {state.transaction?.description}"
              </span>
              .
            </>
          }
          icon={
            <div className="p-2 bg-black-400/10 rounded-full flex justify-center items-center">
              <Trash size={24} />
            </div>
          }
        />
      );
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={hide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        {state.mode === "create" && (
          <TransactionForm
            onSubmit={handleCreate}
            onCancel={hide}
            isSubmitting={createTransactionMutation.isLoading}
            showQuickCategories
          />
        )}
        {state.mode === "edit" && state.transaction && (
          <TransactionForm
            onSubmit={handleEdit}
            onCancel={hide}
            isSubmitting={editTransactionMutation.isLoading}
            defaultValues={{
              ...state.transaction,
              occurred: dayjs(state.transaction?.occurred).toDate(),
              category:
                Number((state.transaction?.category as Category)?.id) ?? 0,
            }}
          />
        )}
        {state.mode === "delete" && (
          <div>
            <DialogFooter>
              <Button className="w-full" variant="outline" onClick={hide}>
                Cancel
              </Button>
              <Button
                className="w-full"
                variant="destructive"
                onClick={handleDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionModal;
